import { EMPTY_TEXT } from '@mitchell/react.component.ux';
import { PhotoCaptureAllowedMimeTypeConstant, PhotoCaptureAttributeType, PhotoCaptureVideoMimeTypesConstant, PhotoCaptureFileExtensionAllowedForGallery, getFolderModelInfo, getDamageGroupFolderDescription } from "../../../domain/photoCapture";
import { TranslationService } from "../../../domain/common";
import { ConvertBytesToMegaBytes } from "../../../domain/common";
import { FileUploadError } from "../../../domain/file";
import { FileUploadErrorMessage } from '../constants';
import { PhotoCaptureAnswerControlType } from '../enums';
export const getWarningMessageByErrorType = (uploadError, maxImageFileSize, maxVideoFileSize, answerControlType = '', questionAttributeType = '') => {
    let message = '';
    switch (uploadError) {
        case FileUploadError.INVALID_FILE_TYPE:
            message = maxVideoFileSize
                ? `${TranslationService.translate(FileUploadErrorMessage.INVALID_FILE_TYPE)} *.${PhotoCaptureVideoMimeTypesConstant.join(', *.').toLowerCase()}.`
                : getMessageForInvalidFileType(answerControlType, questionAttributeType);
            break;
        case FileUploadError.MAX_FILE_SIZE_EXCEEDED:
            message = `${TranslationService.translate(FileUploadErrorMessage.MAX_FILE_SIZE_EXCEEDED)} ${ConvertBytesToMegaBytes(maxImageFileSize)} MB.`;
            break;
        case FileUploadError.MAX_VIDEO_FILE_SIZE_EXCEEDED:
            message = maxVideoFileSize
                ? `${TranslationService.translate(FileUploadErrorMessage.MAX_FILE_SIZE_EXCEEDED)} ${ConvertBytesToMegaBytes(maxVideoFileSize)} MB.`
                : '';
            break;
    }
    return message;
};
export const getMessageForInvalidFileType = (answerControlType = '', questionAttributeType = '') => {
    return answerControlType === PhotoCaptureAnswerControlType.ADD_ATTACHMENT &&
        questionAttributeType === PhotoCaptureAttributeType.DEFAULT
        ? `${TranslationService.translate(FileUploadErrorMessage.INVALID_FILE_TYPE)} *.${PhotoCaptureFileExtensionAllowedForGallery.join(', *.').toLowerCase()}.`
        : `${TranslationService.translate(FileUploadErrorMessage.INVALID_FILE_TYPE)} *.${PhotoCaptureAllowedMimeTypeConstant.join(', *.').toLowerCase()}.`;
};
/**
 * Returns the deactivated/renamed damages.
 * @param { IPhotoCaptureAttachmentFileInfo } image - The image attachment to check for damage name
 * @param { string[] } missingDamages - Array to collect missing folder names
 * @param { string } language - Current language code
 * @returns { string[] } - The list of missingDamages
 */
const getMissingDamages = (attachment, missingDamages, language) => {
    const attachmentFolderDesc = attachment.folderModelInfo && attachment.folderModelInfo.folderAlias
        ? getDamageGroupFolderDescription(attachment.folderModelInfo.folderAlias, language)
        : EMPTY_TEXT;
    return attachment.isParentImage && attachmentFolderDesc ? [...missingDamages, attachmentFolderDesc] : missingDamages;
};
/**
 * Returns an attachment with default (empty) damage data fields. Clears damage name, damage ID, and folder model information.
 * @param { IPhotoCaptureAttachmentFileInfo } attachment - The photo capture attachment file to process
 * @returns { IPhotoCaptureAttachmentFileInfo } - A new image object with default damage data
 */
const getAttachmentWithDefaultDamageData = (attachment) => (Object.assign(Object.assign({}, attachment), { damageName: EMPTY_TEXT, damageId: EMPTY_TEXT, folderModelInfo: undefined }));
/**
 * Processes attachments to update damage information based on available folders
 * @param {IPhotoCaptureAttachmentFileInfo[]} attachments - Array of attachments to process
 * @param {IPhotoCaptureGetFoldersResponse[]} folderOptionList - Available folder options
 * @param {boolean} isFolderServiceCompleted - Flag to deletermine the folder service completion
 * @param {string} language - Current language code
 * @returns {IGuidedPhotoCaptureAttachmentDamageProps} - Attachments with updated damage value and missing folders that are deactivated or renamed
 */
export const getUpdatedAttachmentsDamageInfo = (attachments, folderOptionList, isFolderServiceCompleted, language) => {
    // Return if folder service isn't complete
    if (!isFolderServiceCompleted) {
        return { attachments: [], missingDamages: [] };
    }
    let missingDamages = [];
    let updatedAttachments = [];
    if (attachments && attachments.length) {
        updatedAttachments = attachments.map((attachment) => {
            // When no folders are available, clear all damage info
            if (!folderOptionList || folderOptionList.length === 0) {
                missingDamages = getMissingDamages(attachment, missingDamages, language);
                return getAttachmentWithDefaultDamageData(attachment);
            }
            // Skip the attachments if no damageId
            if (!attachment.damageId) {
                return attachment;
            }
            const matchingFolder = folderOptionList.find(folder => folder.folderTypeId === attachment.damageId);
            // No matching folder found
            if (!matchingFolder) {
                missingDamages = getMissingDamages(attachment, missingDamages, language);
                return getAttachmentWithDefaultDamageData(attachment);
            }
            else {
                // Process folder description based on language
                // Get folder description from folder service based on current language
                const serviceFolderDesc = getDamageGroupFolderDescription(matchingFolder.folderAlias, language);
                // Get folder description from attachment's folder alias using current language
                const attachmentFolderDesc = attachment.folderModelInfo && attachment.folderModelInfo.folderAlias
                    ? getDamageGroupFolderDescription(attachment.folderModelInfo.folderAlias, language)
                    : EMPTY_TEXT;
                // Update if the attachment's folder description and the service folder description don't match
                if (attachmentFolderDesc !== serviceFolderDesc) {
                    missingDamages = getMissingDamages(attachment, missingDamages, language);
                    return Object.assign(Object.assign({}, attachment), { damageName: serviceFolderDesc, folderModelInfo: getFolderModelInfo(matchingFolder.folderTypeId, folderOptionList) });
                }
            }
            return attachment;
        });
    }
    return { attachments: updatedAttachments, missingDamages };
};
//# sourceMappingURL=utilities.js.map